import { useCallback, useRef, type MutableRefObject } from 'react';
import { useGesture } from 'react-use-gesture';

export type SwipeCountParams = {
    /** Пройденное расстояние по оси x за весь жест, px (по модулю) */
    distance: number;
    /** Длительность жеста от начала до отпускания, ms */
    elapsedTime: number;
};

export interface DragProps {
    setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
    canMoveBackward: boolean;
    canMoveForward: boolean;
    moveBackward: (count?: number) => void;
    moveForward: (count?: number) => void;
    gestureProps: Omit<GestureProps, 'active' | 'dx'>;
    /**
     * Если передан снаружи — используется вместо внутреннего рефа для отслеживания драга,
     * чтобы потребитель мог синхронно читать hasDraggedRef.current (например, в своём onClick)
     */
    hasDraggedRef?: MutableRefObject<boolean>;
    /**
     * Коллбэк для расчёта количества слайдов, которые нужно пролистнуть по свайпу — например,
     * чтобы резкий свайп листал больше слайдов, чем медленный (инерция свайпа). Получает сырые
     * параметры жеста (`distance`/`elapsedTime`) и должен вернуть количество слайдов — округление
     * и минимум в 1 слайд остаются на стороне потребителя.
     * Если не передан — свайп всегда переключает на 1 слайд
     * через moveBackward/moveForward (прежнее поведение).
     */
    getSwipeCount?: (params: SwipeCountParams) => number;
}

type GestureProps = {
    slides: React.RefObject<Map<number, HTMLDivElement>>;
    active: boolean;
    dx: number;
    setTranslateX: React.Dispatch<React.SetStateAction<number>>;
    translateX: number;
    setCurrentSlideIndex: (slideIndex: number) => void;
    lastSwipeSlideIndex: number;
};
const useGestureHandler = function () {
    return useCallback(
        ({
            active,
            dx,
            setTranslateX,
            translateX,
            setCurrentSlideIndex,
            slides,
            lastSwipeSlideIndex,
        }: GestureProps) => {
            if (!slides.current) {
                return;
            }

            // Логика задействуется при перетаскивании элемента
            if (active) {
                setTranslateX(prevState => {
                    const dragStep = prevState + dx;
                    const lastElement = slides.current?.get(lastSwipeSlideIndex);

                    const lastElementPositionAndWidthSum = lastElement ? lastElement.offsetLeft * -1 : 0;

                    if (dragStep > 0 || dragStep < lastElementPositionAndWidthSum) {
                        return prevState;
                    }

                    return dragStep;
                });

                return;
            }

            // Логика задействуется при отпускании кнопки мыши или тача
            const goal = translateX * -1;

            if (slides.current.size > 0) {
                // Находим ближайший элемент к которому должны передвинуться
                const closestElement = Array.from(slides.current).reduce(function (prev, curr) {
                    const currOffset = curr?.[1]?.offsetLeft;
                    const prevOffset = prev?.[1]?.offsetLeft;

                    return Math.abs(currOffset - goal) < Math.abs(prevOffset - goal) ? curr : prev;
                });

                setCurrentSlideIndex(closestElement[0]);
            } else {
                setCurrentSlideIndex(0);
            }
        },
        [],
    );
};

/** Свайп по умолчанию всегда листает на 1 слайд (прежнее поведение, если getSwipeCount не передан) */
const defaultGetSwipeCount = () => 1;

export const useDrag = function ({
    setIsDragging,
    canMoveBackward,
    canMoveForward,
    moveBackward,
    moveForward,
    gestureProps,
    hasDraggedRef,
    getSwipeCount = defaultGetSwipeCount,
}: DragProps) {
    const gestureHandler = useGestureHandler();
    const internalHasDragged = useRef(false);
    const hasDragged = hasDraggedRef ?? internalHasDragged;

    return useGesture(
        {
            onDrag: ({ delta: [dx], active, first, last, swipe: [swipeX], movement: [mx], elapsedTime }) => {
                if (first) {
                    hasDragged.current = true;
                }
                // Данное условие необходимо чтобы onClick не срабатывал когда мы отпускаем элемент
                if (last) {
                    setTimeout(() => (hasDragged.current = false), 0);
                }

                setIsDragging(active);

                // Логика свайпа
                // Более подробная информация о том как работает swipe описана тут:
                // https://use-gesture.netlify.app/docs/state/#swipe-drag-only
                if (swipeX !== 0) {
                    // Количество слайдов для пролистывания по свайпу: по умолчанию 1 (прежнее
                    // поведение), либо считается потребителем через getSwipeCount (например,
                    // инерция свайпа — чем резче свайп, тем больше слайдов пролистывается).
                    // Округление и минимум в 1 слайд — ответственность потребителя getSwipeCount.
                    // Используем movement/elapsedTime (пересчитываются на отпускании), а не
                    // `state.velocity` — оно берётся из последнего move-кадра и не пересчитывается
                    // на отпускании, из-за чего для короткого резкого свайпа может быть занижено
                    const swipeCount = getSwipeCount({ distance: Math.abs(mx), elapsedTime });

                    // В случае если мы свайпаем вправо, то swipeX равен 1
                    // Благодоря чему мы понимаем что нужно перелестнуть назад
                    if (swipeX === 1 && canMoveBackward) {
                        moveBackward(swipeCount);
                        // В случае если мы свайпаем влево, то swipeX равен -1
                        // Благодоря чему мы понимаем что нужно перелестнуть вперед
                    } else if (swipeX === -1 && canMoveForward) {
                        moveForward(swipeCount);
                    }
                } else {
                    gestureHandler({
                        active,
                        dx,
                        ...gestureProps,
                    });
                }
            },
            onClickCapture: event => {
                if (hasDragged.current) {
                    event.preventDefault();
                    event.stopPropagation();
                }
            },
        },
        {
            drag: {
                axis: 'x',
                // если смещение по оси x меньше 3px, то useDrag вызываться не будет
                // Необходимо для идентификации обычного клика
                filterTaps: true,
                // По умолчанию react-use-gesture считает свайпом только жест, прошедший
                // одновременно и порог скорости, и порог дистанции (60px). При инерции
                // свайпа резкий, но короткий по дистанции свайп должен листать наравне
                // с длинным — поэтому порог по дистанции убираем, оставляя только скорость
                ...(getSwipeCount !== defaultGetSwipeCount ? { swipeDistance: 0 } : null),
            },
        },
    );
};
